import { ExtendedPlatformContext } from '../../../../types/platformApi';
import { AppInstallOption } from '@wix/editor-platform-host-integration-apis';
import { BIReporter } from './bi';
import { installUnifiedComponents } from './installComponents';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';
import {
  AppRouter,
  ComponentType,
  EditorReactComponent,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { BuilderComponent } from '@wix/document-services-types';
import {
  AddAppData,
  AppData,
  UnifiedComponentsCollection,
  UnifiedPage,
  UnifiedLightbox,
  UnifiedWidget,
  UnifiedComponentAddedToTheStageData,
} from '../../../../types/unifiedComponents';
import { CodePackageComponentData } from '@wix/ambassador-app-service-webapp/types';
import { autoPopulatePlugins } from './addPlugins/autoPopulatePlugins';
import { autoPopulateHeaderPlugins } from './addPlugins/autoPopulateHeaderPlugins';
import { TOKEN } from './addComponents/addUnifiedComponents';
import { applyPresetsToAppComponents, runEditorScript } from './utils';
import {
  serializePages,
  serializeRouters,
  serializeIds,
  serializeCodePackages,
} from './biCollectionSerializers';

function getUnifiedComponentsCollection(
  appData: AppData,
): Promise<UnifiedComponentsCollection> {
  const unifiedComponents: UnifiedComponentsCollection = {
    builderComponents: {},
    codePackages: [],
    pages: {},
    routers: {},
    widgets: {},
  };

  return new Promise((resolve) => {
    appData.components?.forEach((component) => {
      const { data, type, componentId, name } = component;
      if (!data) {
        return;
      }
      switch (type) {
        case ComponentType.APP_ROUTER:
          if (componentId) {
            unifiedComponents.routers[componentId] = {
              data: data as AppRouter,
              extensionId: componentId,
            };
          }
          break;
        case ComponentType.STUDIO_WIDGET:
        case ComponentType.CUSTOM_ELEMENT_WIDGET:
          if (componentId) {
            unifiedComponents.widgets[componentId] = {
              ...data,
              type,
              widgetId: componentId,
            } as unknown as UnifiedWidget;
          }
          break;
        case ComponentType.EDITOR_REACT_COMPONENT:
          if (componentId) {
            const builder = component as BuilderComponent;
            unifiedComponents.builderComponents[componentId] = {
              data: data as unknown as EditorReactComponent,
              type,
              componentId,
              name: name ?? '',
              version: builder.version,
              externalId: builder.externalId,
            };
          }
          break;
        case ComponentType.UNIFIED_LIGHTBOX:
          if (componentId) {
            unifiedComponents.pages[componentId] = {
              data: data as UnifiedLightbox,
              type: ComponentType.UNIFIED_LIGHTBOX,
            };
          }
          break;
        case ComponentType.UNIFIED_PAGE:
          if (componentId) {
            unifiedComponents.pages[componentId] = {
              data: data as UnifiedPage,
              type: ComponentType.UNIFIED_PAGE,
            };
          }
          break;
        case ComponentType.CODE_PACKAGE:
          unifiedComponents.codePackages.push(data as CodePackageComponentData);
          break;
        default:
          break;
      }
    });
    resolve(unifiedComponents);
  });
}

export function installSingleUnifiedComponentsApp(
  context: ExtendedPlatformContext,
  appData: AppData,
  biReporter: BIReporter,
  appOptions?: AppInstallOption,
  finishInstallationCallback?: (appDefId: string, data: AddAppData) => void,
  skipEditorScript?: boolean,
) {
  let collection: UnifiedComponentsCollection;
  let componentsAddedToTheStage: UnifiedComponentAddedToTheStageData = {
    pages: {},
    widgets: {},
  };

  const installAppUnifiedComponents = () =>
    installUnifiedComponents(
      context,
      biReporter,
      appData,
      collection,
      appOptions,
    );

  const setupIntentInstallConfig = async () => {
    await context.intentInstallServices.setupIntentInstallWidgetsConfigFromUnifiedCollection(
      collection,
      appData.appDefinitionName,
    );
  };

  function installCodePackages(): Promise<void> {
    return biReporter.withBI(
      'INSTALL_CODE_PACKAGES',
      { appDefinitionId: appData.appDefinitionId },
      function installCodePackagesIfNeeded() {
        return new Promise<void>((resolve, reject) => {
          if (collection.codePackages.length > 0) {
            context.documentServices.wixCode.codePackages
              .installCodeReusePkg(
                appData.appDefinitionId,
                appData?.appFields?.installedVersion as string,
              )
              .then(resolve)
              .catch((error) =>
                reject(
                  createUnifiedComponentsInstallationError(
                    ErrorType.addingCodePackagesFailed,
                    'Could not install code packages',
                  )
                    .withAppDefIds(appData.appDefinitionId)
                    .withParentError(error),
                ),
              );
          } else {
            resolve();
          }
        });
      },
    );
  }

  function runEditorScriptWithBI(): Promise<void> {
    return biReporter.withBI(
      'PLATFORM_RUN_SCRIPTS',
      { appDefinitionId: appData.appDefinitionId },
      () =>
        runEditorScript(
          context.documentServices,
          appData.appDefinitionId,
          appOptions,
        ),
    );
  }

  function applyInstallationPresets() {
    return new Promise<void>((resolve) => {
      applyPresetsToAppComponents(
        context,
        context.documentServices,
        appData.appDefinitionId,
      );
      resolve();
    });
  }

  function navigateToAppEndPage() {
    return new Promise<void>((resolve, reject) => {
      const resolveWhenNavigationDone = () => {
        resolve();
        biReporter.reportBI('NAVIGATE_TO_DEV_CENTER', {
          appDefinitionId: appData.appDefinitionId,
        });
      };

      const rejectWhenNavigationFailed = () =>
        reject(
          createUnifiedComponentsInstallationError(
            ErrorType.navigateToAppEndPageFailed,
            'Could not locate the component or navigate to the page after installation',
          ).withAppDefIds(appData.appDefinitionId),
        );

      const getWidgetToNavigate = () => {
        return Object.values(componentsAddedToTheStage.widgets)[0];
      };

      const getPageIdToNavigate = () => {
        return Object.values(componentsAddedToTheStage.pages)[0].id;
      };

      if (appOptions?.isSilent) {
        resolve();
        return;
      }

      if (Object.values(componentsAddedToTheStage.widgets).length > 0) {
        context.platformApiMethods.editor.selection
          .locateAndHighlightComponentByCompRef(appData, TOKEN, {
            compRef: getWidgetToNavigate(),
          })
          .then(resolveWhenNavigationDone)
          .catch(rejectWhenNavigationFailed);
        return;
      } else if (Object.values(componentsAddedToTheStage.pages).length > 0) {
        context.documentServices.pages.navigateTo(
          getPageIdToNavigate(),
          resolveWhenNavigationDone,
          rejectWhenNavigationFailed,
        );
        return;
      } else {
        resolve();
      }
    });
  }

  function handlePlugins(): Promise<void> {
    return autoPopulatePlugins(
      context,
      appOptions!,
      appData.appDefinitionId,
      appData.components,
    );
  }

  function handleHeaderPlugins(): Promise<void> {
    return autoPopulateHeaderPlugins(
      context,
      appOptions!,
      appData.appDefinitionId,
      appData.components,
    );
  }

  // TODO: AddAppData - callback with relevant data (success \ type)
  function triggerAppInstalledCallback() {
    const addAppData = {
      ...appData,
      success: true,
      type: 'unified-app',
    };
    return new Promise<AddAppData>((resolve) => {
      finishInstallationCallback?.(appData.appDefinitionId, addAppData);
      resolve(addAppData);
    });
  }

  return new Promise<AddAppData>((resolve, reject) =>
    getUnifiedComponentsCollection(appData)
      .then((componentsCollection) => {
        collection = componentsCollection;
        biReporter.reportBI('APP_COLLECTION_REPORT', {
          appDefinitionId: appData.appDefinitionId,
          name: appData.appDefinitionName,
          app_type: 'unified-app',
          pages: serializePages(collection.pages),
          routers: serializeRouters(collection.routers),
          widgets: serializeIds(collection.widgets),
          builder_components: serializeIds(collection.builderComponents),
          code_packages: serializeCodePackages(collection.codePackages),
        });
      })
      .then(setupIntentInstallConfig)
      .then(installAppUnifiedComponents)
      .then((addToStageValues) => {
        componentsAddedToTheStage = addToStageValues;
      })
      .then(installCodePackages)
      .then(skipEditorScript ? () => Promise.resolve() : runEditorScriptWithBI)
      .then(applyInstallationPresets)
      .then(navigateToAppEndPage)
      .then(handleHeaderPlugins)
      .then(handlePlugins)
      .then(triggerAppInstalledCallback)
      .then(resolve)
      .catch(reject),
  );
}
